import Cookies from 'js-cookie';
import {persinNum} from 'utils/RegExPatterns';
import {v4 as uuidv4} from 'uuid';

export const weekNames = ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه'];

const num2en = (str: string): string => {
  for (let i = 0; i < 10; i++) {
    str = str.replace(persinNum[i], String(i));
  }
  return str;
};

export const isEnglishOnly = (string: string): boolean => {
  const regex = /^[A-Za-z\s]+$/;
  return regex.test(string);
};

export const mobileValidation = (str: string | number): boolean => {
  const mobiles = num2en(str + '');
  const phoneNumberRegex = /^09\d{9}$/;
  return phoneNumberRegex.test(mobiles);
};

export const numberFormat = (value: number): string => {
  return value.toFixed(0).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
};

export function formatStartDate(date: Date): string {
  return date.toLocaleDateString('en-GB').split('/').reverse().join('-') + ' 00:00:00';
}

export function formatEndDate(date: Date): string {
  date.setHours(23, 59, 59, 999);
  return date.toLocaleDateString('en-GB').split('/').reverse().join('-') + ' 23:59:59';
}

export function formattedDate(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function changeDateFormat(date: string): string {
  const parts = date.split('-');
  const month = parseInt(parts[1], 10).toString();
  const day = parseInt(parts[2], 10).toString();
  return month + '/' + day;
}

export function p2e(charOrNumber: string): string {
  const farsiDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  const englishDigits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

  let convertedString = '';
  for (let i = 0; i < charOrNumber.length; i++) {
    const char = charOrNumber[i];
    const digitIndex = farsiDigits.indexOf(char);
    if (digitIndex !== -1) {
      convertedString += englishDigits[digitIndex];
    } else {
      convertedString += char;
    }
  }

  return convertedString;
}

export const isEmptyObject = (obj: Record<string, unknown>): boolean => {
  return Object.keys(obj).length === 0;
};

export const isSessionExpired = (sessionId: string): boolean => {
  const startTime = sessionStorage.getItem(sessionId);
  if (!startTime) {
    return true;
  }
  return false;
};

export const generateUUID = (digits: number): string => {
  const str = `0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ`;
  const uuid: string[] = [];
  for (let i = 0; i < digits; i++) {
    uuid.push(str[Math.floor(Math.random() * str.length)]);
  }
  return uuid.join('');
};

export const getDaysDifference = (date1: string | Date, date2: string | Date): number => {
  const oneDay = 24 * 60 * 60 * 1000;
  const firstDate = new Date(date1);
  const secondDate = new Date(date2);
  const diffDays = Math.round(Math.abs((firstDate.getTime() - secondDate.getTime()) / oneDay));
  return diffDays;
};

export const contentTypeToPostFix = (contentType: string): string => {
  return contentType.split('/').pop() ?? '';
};

export const checkPwaStatus = (): boolean => {
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  const isFullscreen = window.matchMedia('(display-mode: fullscreen)').matches;
  const isIOSStandalone =
    'standalone' in window.navigator &&
    (window.navigator as Navigator & {standalone?: boolean}).standalone === true;

  const isPWA = isStandalone || isFullscreen || isIOSStandalone;
  return isPWA;
};

export const sanitizeRedirect = (path: string, fallback = '/'): string => {
  try {
    const decoded = decodeURIComponent(path);
    if (!decoded.startsWith('/') || decoded.startsWith('//')) {
      return fallback;
    }
    if (/^\/[a-zA-Z][a-zA-Z0-9+\-.]*:/i.test(decoded)) {
      return fallback;
    }
    const url = new URL(decoded, 'https://safe.internal');
    if (url.origin !== 'https://safe.internal') {
      return fallback;
    }
    return decoded;
  } catch {
    return fallback;
  }
};

export const generatedUDID = (name: string, ttl: number): string => {
  const udid = Cookies.get(name);

  if (udid) {
    return udid;
  }

  const newUdid = uuidv4();
  Cookies.set(`${name}`, newUdid, {expires: ttl});
  return newUdid;
};

export const getSelectedVendorFromStorage = (): Record<string, unknown> | null => {
  const raw = localStorage.getItem('selectedVendor');
  if (!raw || raw === 'undefined' || raw === 'null') {
    if (raw) {
      localStorage.removeItem('selectedVendor');
    }
    return null;
  }
  try {
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    localStorage.removeItem('selectedVendor');
    return null;
  }
};

export const convertToPng = async (file: File): Promise<File> => {
  const img = new Image();
  const objectUrl = URL.createObjectURL(file);

  try {
    img.src = objectUrl;
    await img.decode();

    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get canvas context');
    ctx.drawImage(img, 0, 0);

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) throw new Error('Could not convert image to blob');

    return new File([blob], file.name.replace(/\.\w+$/, '.png'), {type: 'image/png'});
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};
